import { motion } from "framer-motion";
import { ArrowUpRight, Clock } from "lucide-react";
import { PLATFORMS, toEmbed } from "../../data/site";

const DEFAULT = PLATFORMS;
import { Reveal, Eyebrow } from "./Reveal";

const Bars = ({ color, live }) => (
  <div className="flex h-8 items-end gap-[3px]">
    {Array.from({ length: 14 }).map((_, i) => (
      <motion.span key={i} className="w-[3px] rounded-full" style={{ background: color, opacity: live ? 1 : 0.3 }}
        animate={live ? { height: ["20%", `${30 + ((i * 37) % 70)}%`, "25%"] } : { height: "25%" }}
        transition={{ duration: 0.9 + (i % 4) * 0.2, repeat: Infinity, ease: "easeInOut" }} />
    ))}
  </div>
);

const Widget = ({ p, i, big }) => {
  const live = Boolean(p.url);
  const embed = toEmbed(p.key, p.url);
  return (
    <motion.div whileHover={{ y: -6 }} transition={{ type: "spring", stiffness: 260, damping: 20 }}
      data-testid={`stream-widget-${p.key}`}
      className={`group relative flex flex-col justify-between overflow-hidden rounded-[28px] border border-[var(--line)] bg-[var(--ink-2)] p-6 sm:p-8 ${big ? "min-h-[320px] md:col-span-2 md:row-span-2" : "min-h-[240px]"}`}>
      <div className="pointer-events-none absolute -right-20 -top-20 h-56 w-56 rounded-full opacity-0 blur-[70px] transition-opacity duration-500 group-hover:opacity-40" style={{ background: p.color }} />
      <div className="relative flex items-start justify-between">
        <span className="font-mono text-xs text-zinc-400">0{i + 1}</span>
        <span className={`flex items-center gap-2 rounded-full border px-3 py-1 font-mono text-xs uppercase tracking-[0.15em] ${live ? "border-[var(--cash)] text-[var(--cash)]" : "border-[var(--line)] text-zinc-400"}`}>
          {live ? <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-[var(--cash)]" /> : <Clock size={12} />}
          {live ? "Live" : "Coming soon"}
        </span>
      </div>
      {embed && <iframe title={p.name} src={embed} className="relative my-4 h-[152px] w-full rounded-xl" allow="autoplay; encrypted-media" loading="lazy" />}
      <div className="relative mt-8">
        <Bars color={p.color} live={live} />
        <h3 className={`mt-4 font-display uppercase leading-none break-words ${big ? "text-6xl sm:text-8xl" : "text-4xl lg:text-5xl"}`}>{p.name}</h3>
        {live ? (
          <a data-testid={`stream-link-${p.key}`} href={p.url} target="_blank" rel="noopener noreferrer"
            className="mt-6 inline-flex items-center gap-2 rounded-full px-5 py-3 font-mono text-xs font-bold uppercase tracking-[0.2em] text-[var(--ink)] transition-transform hover:scale-105"
            style={{ background: p.color }}>
            {p.cta} <ArrowUpRight size={16} />
          </a>
        ) : (
          <p className="mt-6 font-mono text-xs uppercase tracking-[0.2em] text-zinc-400">Dropping on {p.name} soon</p>
        )}
      </div>
    </motion.div>
  );
};

export const Streaming = ({ platforms = DEFAULT }) => (
  <section id="stream" data-testid="streaming-section" className="px-4 py-24 sm:px-8 sm:py-32 lg:px-16">
    <div className="mx-auto max-w-7xl">
      <Reveal>
        <Eyebrow num="02">Stream hub</Eyebrow>
        <h2 className="mt-6 max-w-3xl font-display text-5xl uppercase leading-[0.9] sm:text-7xl">Every platform. <span className="font-serif-i normal-case text-[var(--cash)]">One bag.</span></h2>
      </Reveal>
      <div className="mt-14 grid auto-rows-auto grid-cols-1 gap-5 md:grid-cols-4">
        {platforms.map((p, i) => (
          <Reveal key={p.key} delay={i * 0.06} className={i === 0 ? "md:col-span-2 md:row-span-2 [&>div]:h-full" : "[&>div]:h-full"}>
            <Widget p={p} i={i} big={i === 0} />
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);
